import axios from "./axios";

export async function fetchAccountingLedger({
  year,
  dateFrom,
  dateTo,
  query,
  voucherTypeIds,
  page,
  perPage,
  sort,
  sortBy,
} = {}) {
  const params = {};
  if (year) params.year = year;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (query) params.query = query;
  if (voucherTypeIds?.length) params.voucher_type_ids = JSON.stringify(voucherTypeIds);
  if (page) params.page = page;
  if (perPage) params.per_page = perPage;
  if (sort) params.sort = sort;
  if (sortBy) params.sort_by = sortBy;

  return axios
    .get("/company/accounting/ledger", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({
      error: true,
      syncRequired: error.response?.data?.sync_required,
      data: error.response?.data,
    }));
}

export async function fetchAccountingImportStatus() {
  return axios
    .get("/company/accounting/import_status")
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({ error: true, data: error.response?.data }));
}

export async function fetchAccountingResultReport({ year, dateFrom, dateTo } = {}) {
  const params = {};
  if (year) params.year = year;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  return axios
    .get("/company/accounting/result_report", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({
      error: true,
      syncRequired: error.response?.data?.sync_required,
      data: error.response?.data,
    }));
}

export async function fetchAccountingResultReportAccountDetail({
  accountNumber,
  year,
  dateFrom,
  dateTo,
} = {}) {
  const params = { account_number: accountNumber };
  if (year) params.year = year;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  return axios
    .get("/company/accounting/result_report/account_detail", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({
      error: true,
      syncRequired: error.response?.data?.sync_required,
      data: error.response?.data,
    }));
}

export async function importTripletexHours({ fromDate, toDate, userId, async: runAsync = false } = {}) {
  const params = { async: runAsync };
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  if (userId) params.user_id = userId;

  return axios
    .post("/tripletex/import_hours", params)
    .then((response) => ({ data: response.data, error: false }))
    .catch((error) => ({ error: true, data: error.response?.data }));
}

export async function syncTripletexLedger({ year, dateFrom, dateTo, async: runAsync = false } = {}) {
  const params = { async: runAsync };
  if (year) params.year = year;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;

  return axios
    .post("/tripletex/sync_ledger", params)
    .then((response) => ({ data: response.data, error: false }))
    .catch((error) => ({ error: true, data: error.response?.data }));
}

export async function fetchAccountingProjectsSummary({
  year,
  query,
  page,
  perPage,
  sort,
  sortBy,
  groupBy,
} = {}) {
  const params = {};
  if (year) params.year = year;
  if (query) params.query = query;
  if (page) params.page = page;
  if (perPage) params.per_page = perPage;
  if (sort) params.sort = sort;
  if (sortBy) params.sort_by = sortBy;
  if (groupBy) params.group_by = groupBy;

  return axios
    .get("/company/accounting/projects_summary", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({ error: true, data: error.response?.data }));
}

export async function fetchAccountingProjectSummary({
  projectId,
  includeModules = true,
} = {}) {
  const params = {
    project_id: projectId,
    include_modules: includeModules,
  };

  return axios
    .get("/company/accounting/project_summary", { params })
    .then((response) => ({ data: response.data.data, error: false }))
    .catch((error) => ({ error: true, data: error.response?.data }));
}

function normalizePdfLocale(locale) {
  const value = (locale || "nn").toLowerCase();
  if (value === "no" || value.startsWith("nb")) return "nn";
  if (value.startsWith("en")) return "en";
  if (value.startsWith("sv") || value === "se") return "sv";
  if (value.startsWith("pl")) return "pl";
  return value;
}

function extractApiErrorMessage(json, fallback = "Kunne ikke laste ned PDF") {
  if (!json || typeof json !== "object") return fallback;

  if (Array.isArray(json.errors)) {
    const message = json.errors.filter(Boolean).join(", ");
    if (message) return message;
  }

  if (typeof json.errors === "string" && json.errors.trim()) {
    return json.errors.trim();
  }

  if (typeof json.error === "string" && json.error.trim()) {
    return json.error.trim();
  }

  if (typeof json.message === "string" && json.message.trim()) {
    return json.message.trim();
  }

  return fallback;
}

async function parsePdfBlobError(blob, fallback = "Kunne ikke laste ned PDF") {
  try {
    const text = await blob.text();
    if (!text.trim()) return fallback;
    return extractApiErrorMessage(JSON.parse(text), fallback);
  } catch (_) {
    return "Response is not a PDF file";
  }
}

export async function downloadAccountingProjectSummaryPdf({
  projectId,
  year,
  locale,
  includeModules = true,
  filename,
  endpoint = "/company/accounting/project_summary.pdf",
} = {}) {
  const params = {
    project_id: projectId,
    include_modules: includeModules,
    locale: normalizePdfLocale(locale),
  };
  if (year) params.year = year;

  try {
    const response = await axios.get(endpoint, {
      params,
      responseType: "blob",
      headers: { Accept: "application/pdf" },
    });

    const blob = response.data;
    const headerBuffer = await blob.slice(0, 4).arrayBuffer();
    const pdfHeader = String.fromCharCode(...new Uint8Array(headerBuffer));

    if (pdfHeader !== "%PDF") {
      throw new Error(await parsePdfBlobError(blob));
    }

    const arrayBuffer = await blob.arrayBuffer();
    const pdfBlob = new Blob([arrayBuffer], { type: "application/pdf" });
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      filename ||
      `prosjektregnskap-${projectId}-${year || new Date().getFullYear()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      throw new Error(await parsePdfBlobError(err.response.data));
    }

    if (err.message && !err.response) {
      throw err;
    }

    throw new Error(
      extractApiErrorMessage(err.response?.data, err.message || "Kunne ikke laste ned PDF")
    );
  }
}

export async function downloadAccountingDebugExport({
  projectId,
  year,
  filename,
} = {}) {
  const params = {
    project_id: projectId,
    download: true,
  };
  if (year) params.year = year;

  try {
    const response = await axios.get("/company/accounting/debug_export", {
      params,
      responseType: "blob",
      headers: { Accept: "application/json" },
    });

    const blob = response.data;
    const text = await blob.text();
    const json = JSON.parse(text);
    const exportBlob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(exportBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      filename ||
      `accounting-debug-${projectId}-${year || new Date().getFullYear()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        throw new Error(extractApiErrorMessage(json, "Kunne ikke eksportere debug-data"));
      } catch (parseErr) {
        if (parseErr.message && !parseErr.response) throw parseErr;
      }
    }

    throw new Error(
      extractApiErrorMessage(err.response?.data, err.message || "Kunne ikke eksportere debug-data")
    );
  }
}

export async function downloadAccountingResultReportDebugExport({
  year,
  filename,
} = {}) {
  const params = { download: true };
  if (year) params.year = year;

  try {
    const response = await axios.get("/company/accounting/result_report/debug_export", {
      params,
      responseType: "blob",
      headers: { Accept: "application/json" },
    });

    const blob = response.data;
    const text = await blob.text();
    const json = JSON.parse(text);
    const exportBlob = new Blob([JSON.stringify(json, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(exportBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      filename || `result-report-debug-${year || new Date().getFullYear()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        throw new Error(extractApiErrorMessage(json, "Kunne ikke eksportere debug-data"));
      } catch (parseErr) {
        if (parseErr.message && !parseErr.response) throw parseErr;
      }
    }

    throw new Error(
      extractApiErrorMessage(err.response?.data, err.message || "Kunne ikke eksportere debug-data")
    );
  }
}

/**
 * Superuser (platform ops) calculation guide for company accounting summary.
 * @returns {Promise<object>} guide payload
 */
export async function fetchAccountingSummaryCalculationGuide({ year } = {}) {
  const params = {};
  if (year) params.year = year;
  const response = await axios.get("/company/accounting/summary/calculation_guide", {
    params,
  });
  return response.data?.data ?? response.data;
}

export async function downloadAccountingSummaryCalculationGuide({
  year,
  filename,
} = {}) {
  const params = { download: true };
  if (year) params.year = year;

  try {
    const response = await axios.get("/company/accounting/summary/calculation_guide", {
      params,
      responseType: "blob",
      headers: { Accept: "application/json" },
    });

    const blob = response.data;
    const text = await blob.text();
    const json = JSON.parse(text);
    // Match fetchAccountingSummaryCalculationGuide: unwrap API `{ data: ... }` when present.
    const payload = json?.data ?? json;
    const exportBlob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(exportBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      filename ||
      `accounting-summary-guide-${year || new Date().getFullYear()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return payload;
  } catch (err) {
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        throw new Error(
          extractApiErrorMessage(json, "Kunne ikke eksportere beregningsguide")
        );
      } catch (parseErr) {
        if (parseErr.message && !parseErr.response) throw parseErr;
      }
    }

    throw new Error(
      extractApiErrorMessage(
        err.response?.data,
        err.message || "Kunne ikke eksportere beregningsguide"
      )
    );
  }
}

/** @deprecated use importTripletexHours — kept for tripletex-import page */
export async function importHoursFromTripletex({ from_date, to_date, user_id, async: runAsync = false } = {}) {
  return importTripletexHours({
    fromDate: from_date,
    toDate: to_date,
    userId: user_id,
    async: runAsync,
  });
}
